import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, MapPin, Calendar, Trash2, Pause, Play, Flag, X, ExternalLink, DollarSign, Users } from 'lucide-react';
import { JobSkeleton } from './SkeletonLoaders';
import { getStatusColor, isValidArray, renderSkill } from '../../utils/adminUtils';
import { supabase } from '../../utils/supabase';
import { useAuth } from '../../hooks/useAuth';
import { getJobDetailsUrl } from '../../utils/urlHelpers';
import ConfirmationDialog from '../ui/ConfirmationDialog';
import InfoModal from '../ui/InfoModal';

const JobsTab = ({ 
  filteredJobs, 
  isLoading, 
  viewMode,
  onJobUpdate // Callback to refresh jobs list
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [actionLoading, setActionLoading] = useState({});
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', confirmLabel: 'Confirm', action: null, job: null, type: 'warning' });
  const [infoModal, setInfoModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });
  const [selectedJob, setSelectedJob] = useState(null);

  const handleViewDetails = (job) => {
    setSelectedJob(job);
  };

  const handleJobAction = (action, job) => {
    if (action === 'delete') {
      setConfirmModal({
        isOpen: true,
        title: 'Delete Job',
        message: `Are you sure you want to delete "${job.title}"? This action cannot be undone.`,
        confirmLabel: 'Delete',
        action,
        job,
        type: 'danger'
      });
      return;
    }

    if (action === 'suspend') {
      setConfirmModal({
        isOpen: true,
        title: 'Suspend Job',
        message: `Suspend "${job.title}"? This will hide it from job seekers.`,
        confirmLabel: 'Suspend',
        action,
        job,
        type: 'warning'
      });
      return;
    }

    executeAction(action, job);
  };

  const executeAction = async (action, job) => {
    setActionLoading(prev => ({ ...prev, [job.id]: action }));

    try {
      if (action === 'edit') {
        navigate(`/admin/jobs/${job.id}/edit`);
        return;
      }

      if (action === 'flag') {
        const { error: reportError } = await supabase
          .from('reports')
          .insert({
            reported_by: user?.id,
            reported_job: job.id,
            type: 'job',
            reason: 'Flagged by admin for review',
            description: 'Job flagged during admin review',
            status: 'submitted',
            priority: 'medium'
          });

        if (reportError) throw reportError;

        await logAdminAction('job_flagged', job.id, null, 'Job flagged for review');

        if (onJobUpdate) {
          await onJobUpdate();
        }

        setInfoModal({
          isOpen: true,
          title: 'Job Flagged',
          message: `"${job.title}" has been flagged for review.`,
          type: 'success'
        });
        return;
      }

      if (action === 'delete') {
        const { error: deleteError } = await supabase
          .from('jobs')
          .delete()
          .eq('id', job.id);

        if (deleteError) throw deleteError;

        await logAdminAction('job_removed', job.id, null, 'Job deleted by admin');

      } else if (action === 'suspend') {
        const { error: updateError } = await supabase
          .from('jobs')
          .update({ status: 'archived', updated_at: new Date().toISOString() })
          .eq('id', job.id);

        if (updateError) throw updateError;

        await logAdminAction('job_flagged', job.id, null, 'Job suspended by admin');

      } else if (action === 'activate') {
        const { error: updateError } = await supabase
          .from('jobs')
          .update({ status: 'published', updated_at: new Date().toISOString() })
          .eq('id', job.id);

        if (updateError) throw updateError;

        await logAdminAction('job_approved', job.id, null, 'Job activated by admin');
      }

      if (onJobUpdate) {
        await onJobUpdate();
      }

      const successTitles = { delete: 'Job Deleted', suspend: 'Job Suspended', activate: 'Job Activated' };
      const successMessages = {
        delete: `"${job.title}" has been deleted.`,
        suspend: `"${job.title}" has been suspended and is hidden from job seekers.`,
        activate: `"${job.title}" is now active and visible to job seekers.`
      };
      const successTypes = { delete: 'info', suspend: 'warning', activate: 'success' };

      if (successMessages[action]) {
        setInfoModal({
          isOpen: true,
          title: successTitles[action],
          message: successMessages[action],
          type: successTypes[action]
        });
      }

    } catch (error) {
      console.error(`Error performing ${action} on job:`, error);
      setInfoModal({
        isOpen: true,
        title: 'Action Failed',
        message: `Failed to ${action} job. Please try again.`,
        type: 'error'
      });
    } finally {
      setActionLoading(prev => {
        const newState = { ...prev };
        delete newState[job.id];
        return newState;
      });
    }
  };

  // Helper function to log admin actions
  const logAdminAction = async (actionType, jobId, userId, reason) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('admin_actions')
        .insert({
          admin_user_id: user.id,
          action_type: actionType,
          target_job_id: jobId,
          target_user_id: userId,
          reason: reason,
          notes: `Logged from admin_jobs_tab at ${new Date().toISOString()}`
        });
    } catch (error) {
      console.error('Error logging admin action:', error);
    }
  };



  const getFormattedSalaryRange = (job) => {
    if (job.hourly_rate_min && job.hourly_rate_max) {
      return `$${job.hourly_rate_min}-${job.hourly_rate_max}/hr`;
    }
    if (job.budget_min && job.budget_max) {
      return `$${job.budget_min}-${job.budget_max} ${job.currency || 'USD'}`;
    }
    return null;
  };

  const getJobSkills = (job) => {
    // Handle both skills_required (new schema) and required_skills (legacy)
    return job.skills_required || job.required_skills || [];
  };


  if (isLoading) {
    return (
      <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
        {[...Array(6)].map((_, index) => (
          <JobSkeleton key={index} isGrid={viewMode === 'grid'} />
        ))}
      </div>
    );
  }

  if (filteredJobs.length === 0) {
    return (
      <div className="text-center py-12">
        <Briefcase className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No jobs found</h3>
        <p className="mt-1 text-sm text-gray-500">
          Try adjusting your search terms or filters.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Job Description Popup */}
      {selectedJob && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setSelectedJob(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between p-6 border-b">
              <div className="flex-1 pr-4">
                <div className="flex items-center flex-wrap gap-2 mb-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedJob.status)}`}>
                    {(selectedJob.status || 'active').toUpperCase()}
                  </span>
                  {selectedJob.job_type && (
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full font-medium">
                      {selectedJob.job_type.replace('_', ' ').toUpperCase()}
                    </span>
                  )}
                </div>
                <h2 className="text-xl font-bold text-gray-900">{selectedJob.title}</h2>
                <p className="text-gray-600 mt-1">
                  {selectedJob.company_name || selectedJob.employer?.company_name || selectedJob.employer?.full_name || 'Company Name'}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => { setSelectedJob(null); navigate(getJobDetailsUrl(selectedJob)); }}
                  className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                  title="Open full job page"
                >
                  <ExternalLink className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setSelectedJob(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Meta info */}
            <div className="px-6 py-4 border-b bg-gray-50 flex flex-wrap gap-4 text-sm text-gray-600">
              {selectedJob.location && (
                <div className="flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span>{selectedJob.location}</span>
                </div>
              )}
              {getFormattedSalaryRange(selectedJob) && (
                <div className="flex items-center gap-1">
                  <DollarSign className="w-4 h-4 text-green-500" />
                  <span className="font-medium text-green-600">{getFormattedSalaryRange(selectedJob)}</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4 text-gray-400" />
                <span>{selectedJob.applications_count || 0} applications</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span>Posted {new Date(selectedJob.created_at).toLocaleDateString()}</span>
              </div>
            </div>

            {/* Description */}
            <div className="p-6">
              {selectedJob.description ? (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Job Description</h3>
                  <p className="text-gray-700 whitespace-pre-line leading-relaxed">{selectedJob.description}</p>
                </div>
              ) : (
                <p className="text-gray-400 italic">No description provided.</p>
              )}

              {/* Skills */}
              {isValidArray(getJobSkills(selectedJob)) && (
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Required Skills</h3>
                  <div className="flex flex-wrap gap-2">
                    {getJobSkills(selectedJob).map((skill, index) => (
                      <span key={index} className="px-3 py-1 bg-indigo-50 text-indigo-700 text-sm rounded-full">
                        {renderSkill(skill)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Employer info */}
              {(selectedJob.employer?.full_name || selectedJob.employer?.email) && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Posted By</h3>
                  <p className="text-gray-800 font-medium">{selectedJob.employer?.full_name || '—'}</p>
                  {selectedJob.employer?.email && (
                    <p className="text-gray-500 text-sm">{selectedJob.employer.email}</p>
                  )}
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t bg-gray-50">
              {selectedJob.status === 'published' ? (
                <button
                  onClick={() => { setSelectedJob(null); handleJobAction('suspend', selectedJob); }}
                  className="flex items-center px-4 py-2 text-sm text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors"
                >
                  <Pause className="w-4 h-4 mr-1" /> Suspend
                </button>
              ) : (
                <button
                  onClick={() => { setSelectedJob(null); handleJobAction('activate', selectedJob); }}
                  className="flex items-center px-4 py-2 text-sm text-green-600 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                >
                  <Play className="w-4 h-4 mr-1" /> Activate
                </button>
              )}
              <button
                onClick={() => { setSelectedJob(null); handleJobAction('delete', selectedJob); }}
                className="flex items-center px-4 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </button>
              <button
                onClick={() => setSelectedJob(null)}
                className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmModal.isOpen && (
        <ConfirmationDialog
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          onConfirm={() => {
            const { action, job } = confirmModal;
            setConfirmModal(prev => ({ ...prev, isOpen: false }));
            executeAction(action, job);
          }}
          onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
          isLoading={!!actionLoading[confirmModal.job?.id]}
          type={confirmModal.type}
        />
      )}
      {infoModal.isOpen && (
        <InfoModal
          title={infoModal.title}
          message={infoModal.message}
          type={infoModal.type}
          onClose={() => setInfoModal(prev => ({ ...prev, isOpen: false }))}
        />
      )}
      <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
      {filteredJobs.map((job) => (
        <div 
          key={job.id} 
          className="bg-white rounded-lg shadow-sm border p-6 relative cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => handleViewDetails(job)}
        >
          <div className={`flex ${viewMode === 'grid' ? 'flex-col space-y-4' : 'items-center justify-between'}`}>
            <div className="flex-1">
              {/* Status and job type badges */}
              <div className={`flex items-center ${viewMode === 'grid' ? 'justify-center flex-wrap' : ''} space-x-3 mb-2`}>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                  {(job.status || 'active').toUpperCase()}
                </span>
                {job.job_type && (
                  <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full font-medium">
                    {job.job_type.replace('_', ' ').toUpperCase()}
                  </span>
                )}
                <span className="text-gray-500 text-sm">
                  {new Date(job.created_at).toLocaleDateString()}
                </span>
              </div>

              {/* Job title and company */}
              <h3 className={`text-lg font-semibold text-gray-900 mb-1 ${viewMode === 'grid' ? 'text-center' : ''}`}>
                {job.title}
              </h3>
              <p className={`text-gray-600 mb-2 ${viewMode === 'grid' ? 'text-center' : ''}`}>
                {job.company_name || job.employer?.company_name || job.employer?.full_name || 'Company Name'}
              </p>
              
              {/* Grid view additional details */}
              {viewMode === 'grid' && (
                <div className="text-center text-sm text-gray-600 space-y-2">
                  {job.location && (
                    <div className="flex items-center justify-center space-x-1">
                      <MapPin className="w-4 h-4" />
                      <span>{job.location}</span>
                    </div>
                  )}
                  {getFormattedSalaryRange(job) && (
                    <div className="font-medium text-green-600">
                      {getFormattedSalaryRange(job)}
                    </div>
                  )}
                  {isValidArray(getJobSkills(job)) && (
                    <div className="flex flex-wrap justify-center gap-1 mt-2">
                      {getJobSkills(job).slice(0, 3).map((skill, index) => (
                        <span key={index} className="px-2 py-1 bg-gray-50 text-gray-700 text-xs rounded-full">
                          {renderSkill(skill)}
                        </span>
                      ))}
                      {getJobSkills(job).length > 3 && (
                        <span className="px-2 py-1 bg-gray-50 text-gray-600 text-xs rounded-full">
                          +{getJobSkills(job).length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center justify-center space-x-1 mt-2">
                    <Calendar className="w-4 h-4" />
                    <span>Posted {new Date(job.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              )}

              {/* List view additional details */}
              {viewMode === 'list' && (
                <div className="text-sm text-gray-500">
                  <div className="flex items-center space-x-4">
                    <span>Posted by: <span className="font-medium">{job.employer?.full_name || 'Employer'}</span></span>
                    {getFormattedSalaryRange(job) && (
                      <>
                        <span>•</span>
                        <span className="font-medium text-green-600">{getFormattedSalaryRange(job)}</span>
                      </>
                    )}
                    <span>•</span>
                    <span>{job.applications_count || 0} applications</span>
                    <span>•</span>
                    <span>{job.views_count || 0} views</span>
                    {job.location && (
                      <>
                        <span>•</span>
                        <div className="flex items-center space-x-1">
                          <MapPin className="w-3 h-3" />
                          <span>{job.location}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className={`flex items-center ${viewMode === 'grid' ? 'flex-wrap gap-2 mt-4 justify-center' : 'space-x-2 ml-4'}`}>
              {/* Status toggle button */}
              {job.status === 'published' ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleJobAction('suspend', job);
                  }}
                  className="flex items-center px-3 py-1 text-xs text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors"
                  disabled={actionLoading[job.id] === 'suspend'}
                  title="Suspend Job"
                >
                  <Pause className="w-3 h-3 mr-1" />
                  {actionLoading[job.id] === 'suspend' ? 'Suspending...' : 'Suspend'}
                </button>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleJobAction('activate', job);
                  }}
                  className="flex items-center px-3 py-1 text-xs text-green-600 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                  disabled={actionLoading[job.id] === 'activate'}
                  title="Activate Job"
                >
                  <Play className="w-3 h-3 mr-1" />
                  {actionLoading[job.id] === 'activate' ? 'Activating...' : 'Activate'}
                </button>
              )}

              {/* Flag button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleJobAction('flag', job);
                }}
                className="flex items-center px-3 py-1 text-xs text-yellow-600 bg-yellow-50 hover:bg-yellow-100 rounded-lg transition-colors"
                disabled={actionLoading[job.id] === 'flag'}
                title="Flag Job"
              >
                <Flag className="w-3 h-3 mr-1" />
                {actionLoading[job.id] === 'flag' ? 'Flagging...' : 'Flag'}
              </button>

              {/* Delete button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleJobAction('delete', job);
                }}
                className="flex items-center px-3 py-1 text-xs text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                disabled={actionLoading[job.id] === 'delete'}
                title="Delete Job"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                {actionLoading[job.id] === 'delete' ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
    </>
  );
};

export default JobsTab;
